import { describeRelativeTime } from "@nova/format"
import { useTranslation } from "@nova/use-translation"
import classNames from "classnames"
import {
    selectThumbnailUrlForProject,
    selectUpdatingThumbnail,
} from "features/entity-caching/fires/selectors"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useEffect, useMemo, useRef, useState } from "react"
import { animationClass, defaultProjectName } from "../../../../constants"
import { SmallIcon, Star, StarFill, ThreeDotsSettings } from "@nova/component-icons"
import { ContextMenu } from "../../../../TODO-put-in-package/ContextMenu"
import { Project, WorkspaceEntityType } from "../../../../types"
import {
    selectActivatingProjectId,
    selectActiveWorkspaceId,
    selectIdRenaming,
    setIdRenaming,
} from "../../../../WorkspaceManagementSlice"
import { ClickAwayListener } from "@nova/component-click-away-listener"
import { NovaSpinner } from "@nova/component-nova-spinner"
import { RenamableText } from "features/data-management/workspace/TODO-put-in-package/RenamableText"
import { createContextMenuSections } from "features/data-management/workspace/module"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { useWorkspaceProjectDragAndDrop } from "../../../useWorkspaceProjectDragAndDrop"
import { useLoadingText } from "../../../useLoadingText"
import { WorkspaceCardViewers } from "./WorkspaceCardViewers"
import { setActiveContextMenu } from "features/popups/slice"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { useInView } from "react-intersection-observer"
import { selectFavoritesData } from "features/data-management/workspace/context/selectors"
import {
    toggleFavorite,
    deleteWorkspaceEntity,
    openWorkspaceShareModal,
} from "features/data-management/workspace/context/thunks"
import { updateFire } from "features/entity-caching/fires/thunks"
import { useActivateProject } from "features/data-management/workspace/context/navigation"

const defaultProjectThumbnailUrl = "/images/project-card-default.png"
const ENABLED_WORKSPACE_DRONES = true

export const WorkspaceProjectCard = ({ project }: { project: Project }) => {
    const key = `grid_${project.id}`
    const type = WorkspaceEntityType.Project

    const dispatch = useAppDispatch()
    const { favoriteIds } = useAppSelector(selectFavoritesData)
    const isFavorite = (id: string) => favoriteIds.has(id)
    const activateProject = useActivateProject()

    const drones = project.drones
    const { t } = useTranslation()
    const [isHovering, setIsHovering] = useState(false)
    const { isEditor } = useWorkspacePermissions(project.id)
    const activeGridProjectContextMenu = useAppSelector(selectActiveContextMenu("GridProject"))
    const contextMenuIsForThisCard = activeGridProjectContextMenu?.data.projectId === project.id
    const renameRef = useRef<HTMLInputElement>(null)
    const idRenaming = useAppSelector(selectIdRenaming())
    const isRenaming = idRenaming === key
    const { ref: inViewRef, inView } = useInView()
    const workspaceId = useAppSelector(selectActiveWorkspaceId())

    const isActivatingThisProject = useAppSelector(selectActivatingProjectId()) === project.id
    const thumbnailUpdating = useAppSelector(selectUpdatingThumbnail(project.id))
    const thumbnailUrl =
        useAppSelector(selectThumbnailUrlForProject(project.id)) || defaultProjectThumbnailUrl
    const loadingText = useLoadingText(isActivatingThisProject)

    const contextMenuSections = useMemo(
        () =>
            createContextMenuSections({
                id: project.id,
                key,
                title: project.name || defaultProjectName,
                isFavorite: favoriteIds.has(project.id),
                onToggleFavorite: (id) => dispatch(toggleFavorite(id)),
                onBeginRenaming: isEditor ? (id) => dispatch(setIdRenaming(id)) : undefined,
                onOpenShareModal: (id) => dispatch(openWorkspaceShareModal(id)),
                onOpenInNewTab: (id) => {
                    window.open(`${window.origin}/workspace/${workspaceId}/project/${id}`)
                },
                onDelete: isEditor
                    ? (id, name) => dispatch(deleteWorkspaceEntity({ id, name, type }))
                    : undefined,
            }),
        [project.id, project.name, key, favoriteIds, isEditor, workspaceId, dispatch, type]
    )

    const { dragRef, anythingIsDragging } = useWorkspaceProjectDragAndDrop({
        id: project.id,
        isEditor,
        isRenaming,
    })

    const handleClickAway = () => {
        if (idRenaming) dispatch(setIdRenaming(undefined))
    }

    useEffect(() => {
        if (isRenaming) renameRef.current?.focus()
    }, [isRenaming])

    const handleToggleContextMenu = (force?: boolean) => {
        const shouldHide = (contextMenuIsForThisCard && !force) || force === false
        if (force === false) setIsHovering(false)
        dispatch(
            setActiveContextMenu(
                shouldHide
                    ? undefined
                    : {
                          type: "GridProject",
                          data: { projectId: project.id, fixedPosition: false },
                          position: { top: 0, left: 0 },
                      }
            )
        )
    }

    const shouldShow3DotsButton =
        (isHovering || contextMenuIsForThisCard) && contextMenuSections.length > 0 && !isRenaming

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <div
                ref={(ref) => {
                    dragRef(ref)
                    inViewRef(ref)
                }}
                className={classNames(
                    "relative flex-1 w-full max-w-[20rem] select-none rounded-md bg-transparent",
                    {
                        "cursor-pointer": !isRenaming,
                        "opacity-20": anythingIsDragging,
                    }
                )}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                onClick={
                    !isRenaming ? () => activateProject(project.id) : (e) => e.stopPropagation()
                }
                onContextMenu={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    dispatch(
                        setActiveContextMenu({
                            type: "GridProject",
                            data: { projectId: project.id, fixedPosition: true },
                            position: { top: e.clientY, left: e.clientX },
                        })
                    )
                }}
            >
                {
                    <div className="relative w-full h-[8rem]">
                        {isActivatingThisProject ? (
                            <div className="h-full flex justify-center items-center overflow-hidden rounded-md border border-gray-100">
                                <NovaSpinner className="w-8 h-8" />
                            </div>
                        ) : thumbnailUrl && inView && !thumbnailUpdating ? (
                            <>
                                <img
                                    className={classNames(
                                        animationClass,
                                        "w-full h-full object-cover object-center overflow-hidden rounded-md aspect-[5/4]",
                                        { "opacity-90": !isHovering && !isRenaming }
                                    )}
                                    src={thumbnailUrl as string}
                                    loading="lazy"
                                    alt="project"
                                    width={250}
                                    height={200}
                                />
                                <div
                                    className={classNames(
                                        animationClass,
                                        "absolute inset-0 rounded-md border opacity-90",
                                        {
                                            "border-black-900": !isHovering && !isRenaming,
                                            "border-transparent shadow-card":
                                                isHovering || isRenaming,
                                        }
                                    )}
                                />
                            </>
                        ) : (
                            <div className="w-full shimmer-loading-gradient overflow-hidden rounded-md" />
                        )}

                        <WorkspaceCardViewers
                            viewers={project.viewers}
                            drones={ENABLED_WORKSPACE_DRONES ? drones : []}
                            className="absolute -bottom-3 right-4"
                        />
                    </div>
                }

                <div className="py-3 flex flex-col gap-0.5">
                    <div className="relative flex items-center gap-2 text-gray-300">
                        <small>{t("Edited")}</small>
                        <div className="w-1 h-1 rounded-sm bg-gray-500" />
                        <small>
                            {project.updatedAt ? describeRelativeTime(project.updatedAt) : "Now"}
                        </small>
                        <div className="flex-grow" />

                        {isHovering && (
                            <SmallIcon
                                Icon={!isFavorite(project.id) ? Star : StarFill}
                                className={classNames("cursor-pointer", animationClass, {
                                    "text-gray-300 hover:text-gray-0": !isFavorite(project.id),
                                    "text-yellow-500 hover:text-yellow-200": isFavorite(project.id),
                                })}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    dispatch(toggleFavorite(project.id))
                                }}
                            />
                        )}

                        <SmallIcon
                            Icon={ThreeDotsSettings}
                            className={classNames(
                                "text-gray-300 hover:text-gray-0 cursor-pointer",
                                animationClass,
                                { "opacity-0": !shouldShow3DotsButton }
                            )}
                            onClick={(e) => {
                                e.stopPropagation()
                                handleToggleContextMenu()
                            }}
                        />

                        {contextMenuIsForThisCard && (
                            <ContextMenu
                                sections={contextMenuSections}
                                onBlur={() => handleToggleContextMenu(false)}
                                className="flex-none"
                                isFixedPosition={activeGridProjectContextMenu.data.fixedPosition}
                                style={
                                    !activeGridProjectContextMenu.data.fixedPosition
                                        ? { top: "1.5rem", right: "0" }
                                        : undefined
                                }
                                top={activeGridProjectContextMenu.position.top}
                                left={activeGridProjectContextMenu.position.left}
                            />
                        )}
                    </div>

                    <div className="flex items-center gap-1">
                        <RenamableText
                            ref={renameRef}
                            Tag="h4"
                            title={
                                isActivatingThisProject
                                    ? loadingText
                                    : project.name || defaultProjectName
                            }
                            id={project.id}
                            isRenaming={isRenaming}
                            initialTitleIsPlaceholder={project.name === defaultProjectName}
                            className={classNames("!h-6", animationClass, {
                                "text-gray-100": !isHovering && !isRenaming,
                                "text-gray-0": isHovering || isRenaming,
                                "!w-auto": !isRenaming,
                            })}
                            underlineClassNames={(isRenaming) =>
                                classNames({
                                    "border-blue-500": isRenaming,
                                    "border-transparent": !isRenaming,
                                })
                            }
                            onConfirmRenaming={(id, name) => {
                                dispatch(updateFire({ id, updates: { name } }))
                                dispatch(setIdRenaming(undefined))
                            }}
                            onCancelRenaming={() => dispatch(setIdRenaming(undefined))}
                        />
                        {isFavorite(project.id) && !isRenaming && (
                            <SmallIcon Icon={StarFill} className="text-gray-0" />
                        )}
                    </div>
                </div>
            </div>
        </ClickAwayListener>
    )
}
